import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import TopBar from "./TopBar";

function ProvisionFallback({ message, kind, onRetry, onSignOut }) {
  const canRetry = kind === "error";
  return (
    <div
      className="mt-4 max-w-md border border-stone-200 rounded-lg p-4 bg-white"
      data-testid="provision-fallback"
    >
      <div className="text-sm text-stone-800" data-testid="provision-error-message">{message}</div>
      <div className="flex gap-2 mt-3">
        {canRetry && (
          <button
            onClick={() => onRetry?.()}
            className="border border-stone-300 rounded px-3 py-1.5 text-xs mono hover:bg-stone-50"
            data-testid="provision-retry-btn"
          >
            Retry
          </button>
        )}
        <button
          onClick={() => onSignOut?.()}
          className="border border-stone-800 bg-stone-900 text-white rounded px-3 py-1.5 text-xs mono hover:bg-stone-800"
          data-testid="provision-signout-btn"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function ProtectedShell() {
  const { session, profile, loading, slowRestore, provisionError, signOut, refreshProfile } = useAuth();
  const location = useLocation();

  if (loading)
    return (
      <div className="p-10 mono text-sm" data-testid="auth-loading">
        {slowRestore ? "Restoring your session…" : "Loading…"}
      </div>
    );
  if (!session) {
    // RC-2: preserve the intended destination (e.g. a /scan/pkg/:token QR deep-link) so the
    // user returns to it after signing in.
    const redirect = encodeURIComponent(location.pathname + location.search);
    // Route intent: known Staff-only areas send unauthenticated users to the dedicated Staff
    // login; everything else (Sender app, onboarding, generic /app) stays on the Sender login.
    const p = location.pathname;
    const isStaffArea = p.startsWith("/admin") || p === "/captain" || p.startsWith("/captain/")
      || p === "/rider" || p.startsWith("/rider/") || p.startsWith("/scan/pkg/");
    const base = isStaffArea ? "/staff/login" : "/login";
    return <Navigate to={`${base}?redirect=${redirect}`} replace />;
  }

  if (!profile) {
    // P0 fix: a failed/slow provision now ends in a terminal `provisionError` (bounded retry in
    // lib/auth.jsx), so the shell never loops forever — it shows an actionable Sign out / Retry.
    return (
      <div className="p-10 mono text-sm" data-testid="provisioning-state">
        {provisionError ? (
          <ProvisionFallback
            message={provisionError.message}
            kind={provisionError.kind}
            onRetry={refreshProfile}
            onSignOut={signOut}
          />
        ) : (
          "Provisioning your profile…"
        )}
      </div>
    );
  }

  // Staff MFA gate (frontend): a staff session that is password-authenticated but not yet
  // email-OTP verified must never render the app shell. Redirect to the dedicated staff login
  // (preserving the intended destination). The backend enforces the same rule authoritatively.
  if (profile.mfa_required && !profile.mfa_verified) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/staff/login?redirect=${redirect}`} replace />;
  }

  return (
    <div className="min-h-screen">
      <TopBar />
      <Outlet />
    </div>
  );
}
